import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChoiceSummary,
  SenseSections,
  type ChoicePrice,
  type LookupErrorReason,
  type SenseChoice,
} from '@features/CardsWorkspace';
import { cn } from '@shared/lib';
import { ChevronDownIcon } from '@shared/ui';
import { lookupErrorView } from '../lib/composerModel';

/** Что в листе: ответ словаря, «нет в словаре», ошибка поиска или ручная карточка. */
export type SheetView = 'found' | 'notFound' | 'error' | 'manual';

export interface SheetNotice {
  tone: 'danger' | 'muted';
  text: string;
}

export interface DictionarySheetContent {
  view: SheetView;
  word: string;
  /** Прошлый ответ, пока перечитываем новый (слово или модуль поменяли): приглушён до 50%. */
  stale: boolean;
  transcription: string | null;
  yandex: boolean;
  choice: SenseChoice;
  price: ChoicePrice;
  errorReason: LookupErrorReason | null;
  moduleTitle: string | null;
  notice: SheetNotice | null;
  /** «Добавить вручную», подарок, «Тарифы» — под составом, когда жетоны закончились. */
  exhaustedActions?: ReactNode;
}

interface DictionarySheetProps extends DictionarySheetContent {
  id: string;
  open: boolean;
  maxHeight: number;
  /** Создаём или проверяем исход: отметки неактивны (ТЗ § 6). */
  busy: boolean;
  onCollapse: () => void;
  onRetry: () => void;
  onManual: () => void;
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M4 20l4-1 11-11-3-3L5 16z" />
    </svg>
  );
}

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Лист словаря над строкой (ТЗ главной § 6): одна карточка со строкой, растёт вверх. Высота — по
 * содержимому, не выше `maxHeight`; середина (разделы словаря) прокручивается внутри. Сверху полоса:
 * транскрипция или «ищем в словаре…», пояснение, «по данным Яндекс.Словаря» и «Свернуть» (в DOM
 * кнопка последней — порядок Tab: разделы, итог, «Свернуть»). Внизу общий итог `ChoiceSummary`.
 * На закрытии содержимое держится до конца анимации, чтобы лист не пустел, пока опускается.
 */
export function DictionarySheet(props: DictionarySheetProps) {
  const { t } = useTranslation();
  const { id, open, maxHeight, busy, onCollapse, onRetry, onManual } = props;
  const last = useRef<DictionarySheetContent>(props);
  if (open) last.current = props;
  const content = open ? props : last.current;
  const [mounted, setMounted] = useState(open);
  const [innerHeight, setInnerHeight] = useState(0);
  const [settled, setSettled] = useState(false);
  const innerRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const timer = window.setTimeout(() => setSettled(true), 280);
      return () => window.clearTimeout(timer);
    }
    setSettled(false);
    const timer = window.setTimeout(() => setMounted(false), 240);
    return () => window.clearTimeout(timer);
  }, [open]);

  // Высота листа — по содержимому: новый ответ меняет её плавно (200 мс), без скачка.
  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    setInnerHeight(el.offsetHeight);
    const observer = new ResizeObserver(() => setInnerHeight(el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, [mounted]);

  // Пока создаём, отметки и разделы не нажимаются (ТЗ § 6: «чип и отметки неактивны»).
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.inert = busy || !open;
  }, [busy, open, mounted]);

  // Новый ответ при открытом листе — содержимое сменяется перекрёстно (120 мс).
  const answerKey = content.view === 'found' ? content.choice.view : `${content.view}:${content.word}`;
  const prevKey = useRef(answerKey);
  useEffect(() => {
    if (prevKey.current === answerKey) return;
    prevKey.current = answerKey;
    if (!open || reducedMotion()) return;
    bodyRef.current?.animate?.([{ opacity: 0.35 }, { opacity: 1 }], { duration: 120, easing: 'ease-out' });
  }, [answerKey, open]);

  const { view, word, stale, transcription, yandex, choice, price, errorReason, moduleTitle, notice, exhaustedActions } = content;

  const noticeLine = notice && (
    <p className={cn('text-[14px] leading-[1.4]', notice.tone === 'danger' ? 'text-danger' : 'text-muted')} role="status">
      {notice.text}
    </p>
  );

  // Тот же текст и «Повторить», что в превью телефона (lookupErrorView).
  const errorView = lookupErrorView(errorReason ?? 'unknown');
  const errorText = t(errorView.key);
  const canRetry = errorView.retry;

  return (
    <div
      id={id}
      role="region"
      aria-label={t('homeComposer.sheetAria', { word })}
      aria-hidden={!open}
      className={cn('hc-sheet', open && 'is-open', settled && 'is-settled')}
      style={{ height: open ? innerHeight : 0 }}
    >
      {mounted && (
        <div ref={innerRef} className="hc-sheet-inner relative flex flex-col" style={{ maxHeight }}>
          <div ref={bodyRef} className={cn('flex min-h-0 flex-1 flex-col', stale && 'hc-stale')}>
            {/* Полоса листа: место под «Свернуть» справа (сама кнопка — в конце DOM). */}
            <div className="flex min-h-11 flex-none flex-wrap items-center gap-x-4 gap-y-1 border-b border-solid border-line py-2 pl-5 pr-14">
              {view === 'found' && (
                <>
                  {stale ? (
                    <span className="text-[14px] leading-5 text-muted">{t('cardsWorkspace.pick.searching')}</span>
                  ) : (
                    transcription && (
                      <span lang="en" className="font-serif text-[16px] not-italic leading-5 text-muted">
                        {transcription}
                      </span>
                    )
                  )}
                  <span className="min-w-0 flex-1 text-[14px] leading-5 text-muted">{t('cardsWorkspace.pick.strip')}</span>
                  {yandex && <span className="text-[12px] leading-4 text-muted">{t('cardsWorkspace.pick.yandex')}</span>}
                </>
              )}
              {view === 'notFound' && (
                <span className="text-[15px] font-semibold leading-5 text-fg">
                  {t('cardsWorkspace.pick.notFoundTitle', { word })}
                </span>
              )}
              {view === 'error' && <span className="text-[14px] leading-5 text-fg">{errorText}</span>}
              {view === 'manual' && (
                <span className="text-[14px] leading-5 text-muted">
                  {t('homeComposer.manualLine', { module: moduleTitle ?? '' })}
                </span>
              )}
            </div>

            {view === 'found' && choice.view && (
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-2 [scrollbar-color:#34343a_transparent] [scrollbar-width:thin]">
                <SenseSections choice={choice} />
              </div>
            )}

            {view === 'notFound' && (
              <div className="px-5 pb-4 pt-3">
                <p className="text-[14px] leading-[1.45] text-[#c9c5c0]">{t('cardsWorkspace.pick.notFoundBody')}</p>
                <button
                  type="button"
                  onClick={onManual}
                  className="mt-3 flex min-h-11 w-full items-center gap-2 rounded-[18px] bg-[#19191c] px-3.5 py-2.5 text-left shadow-[inset_0_0_0_1px_#2a2a30] transition-shadow [@media(hover:hover)]:hover:shadow-[inset_0_0_0_1px_#3a3a40]"
                >
                  <PencilIcon className="h-3.5 w-3.5 flex-none text-[#c9c5c0]" />
                  <span className="font-serif text-[17px] font-medium">{t('cardsWorkspace.pick.addManual')}</span>
                  <span className="ml-auto text-[14px] text-muted">{t('cardsWorkspace.pick.addManualHint')}</span>
                </button>
              </div>
            )}

            {view === 'error' && canRetry && (
              <div className="px-5 pb-3 pt-2">
                <button
                  type="button"
                  onClick={onRetry}
                  className="min-h-11 rounded-pill px-1 text-[14px] font-medium text-accent-hi transition-colors hover:text-fg"
                >
                  {t('training.retry')}
                </button>
              </div>
            )}

            {view === 'found' && (
              <div className="flex-none border-t border-solid border-line bg-[#19191c] px-5 py-3">
                <ChoiceSummary
                  choice={choice}
                  price={price}
                  found
                  titleSuffix={
                    moduleTitle ? (
                      <span className="hc-compact-only font-normal text-muted">
                        {' '}
                        {t('homeComposer.inModule', { title: moduleTitle })}
                      </span>
                    ) : null
                  }
                  exhaustedActions={exhaustedActions}
                />
                {noticeLine && <div className="mt-2">{noticeLine}</div>}
              </div>
            )}

            {view !== 'found' && noticeLine && <div className="px-5 pb-3">{noticeLine}</div>}
          </div>

          <button
            type="button"
            onClick={onCollapse}
            aria-label={t('homeComposer.collapse')}
            // Рамка фокуса — внутрь: верх листа обрезает всё, что выходит за его край.
            className="absolute right-1.5 top-0 grid h-11 w-11 place-items-center rounded-full text-muted transition-colors hover:text-fg focus-visible:outline-offset-[-2px]"
          >
            <span aria-hidden className="grid h-8 w-8 place-items-center rounded-full bg-elev">
              <ChevronDownIcon className="h-4 w-4" />
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
